import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Button,
  Grid, Alert, CircularProgress,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PlaceIcon from '@mui/icons-material/Place';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RouteIcon from '@mui/icons-material/Route';
import PaymentsIcon from '@mui/icons-material/Payments';
import vtcService from '../../services/vtc.service';
import MapboxAddressInput from '../../components/MapboxAddressInput';
import MiniMap from '../../components/MiniMap';

/* ── Design tokens ─────────────────────────────────────────── */
const T = {
  purple: '#552583', purpleDark: '#3d1a61', gold: '#FDB927',
  bg: '#E4F0F6', white: '#ffffff', ink: '#1a1a1a', sub: '#4a4a4a', muted: '#7a7a7a',
  border: 'rgba(85,37,131,0.08)',
  radius: { sm: '8px', md: '10px', btn: '12px', card: '20px' },
  transition: '0.15s ease',
  shadow: '0 1px 4px rgba(0,0,0,0.06)',
};

const vehicleTypes = [
  { value: 'economy', label: 'Economy', desc: 'Tarif avantageux', multiplier: 1 },
  { value: 'comfort', label: 'Comfort', desc: 'Confort garanti', multiplier: 1.3 },
  { value: 'premium', label: 'Premium', desc: 'Haut de gamme', multiplier: 1.8 },
  { value: 'xl', label: 'XL', desc: '6+ passagers', multiplier: 1.5 },
];

const VTCRequest = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    pickup_address: '', pickup_latitude: null, pickup_longitude: null,
    dropoff_address: '', dropoff_latitude: null, dropoff_longitude: null,
    vehicle_type: 'comfort',
  });
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handlePickupSelect = (result) => {
    if (!result) { setFormData(p => ({ ...p, pickup_address: '', pickup_latitude: null, pickup_longitude: null })); setEstimate(null); return; }
    setFormData(p => ({ ...p, pickup_address: result.fullAddress || result.place_name || result.address, pickup_latitude: result.latitude, pickup_longitude: result.longitude }));
    setEstimate(null);
  };

  const handleDropoffSelect = (result) => {
    if (!result) { setFormData(p => ({ ...p, dropoff_address: '', dropoff_latitude: null, dropoff_longitude: null })); setEstimate(null); return; }
    setFormData(p => ({ ...p, dropoff_address: result.fullAddress || result.place_name || result.address, dropoff_latitude: result.latitude, dropoff_longitude: result.longitude }));
    setEstimate(null);
  };

  const canEstimate = formData.pickup_latitude && formData.dropoff_latitude;

  const calculateEstimate = async () => {
    if (!canEstimate) { setError('Veuillez sélectionner les adresses de départ et d\'arrivée'); return; }
    setError('');
    try {
      const GKEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/directions/json?origin=${formData.pickup_latitude},${formData.pickup_longitude}&destination=${formData.dropoff_latitude},${formData.dropoff_longitude}&mode=driving&language=fr&key=${GKEY}`
      );
      const data = await res.json();
      const route = data.routes?.[0]?.legs?.[0];
      if (!route) { setError('Impossible de calculer l\'itinéraire'); return; }
      const distanceKm = (route.distance.value / 1000).toFixed(1);
      const durationMin = Math.round(route.duration.value / 60);
      const mult = vehicleTypes.find(v => v.value === formData.vehicle_type)?.multiplier || 1;
      const baseFare = 3;
      const fare = Math.round((baseFare + distanceKm * 2.5 + durationMin * 0.3) * mult);
      setEstimate({ distance: distanceKm, duration: durationMin, fare });
    } catch { setError('Erreur de calcul'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await vtcService.requestRide(formData);
      setSuccess(true);
      setTimeout(() => navigate('/vtc/rides'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la demande');
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 6, bgcolor: T.bg }}>
      <Container maxWidth="sm" sx={{ pt: 4 }}>

        {/* Header */}
        <Box sx={{ mb: 3.5 }}>
          <Typography sx={{ fontWeight: 800, color: T.ink, fontSize: '1.5rem', letterSpacing: '-0.01em' }}>
            Réserver une course
          </Typography>
          <Typography sx={{ color: T.muted, mt: 0.3, fontSize: '0.85rem' }}>
            Choisissez votre destination
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: T.radius.sm }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: T.radius.sm }}>Course demandée avec succès ! Redirection...</Alert>}

        <form onSubmit={handleSubmit}>
          {/* Addresses */}
          <Box sx={{ mb: 2.5, p: 2.5, bgcolor: T.white, borderRadius: T.radius.card, border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 3.5, pb: 1 }}>
                <FiberManualRecordIcon sx={{ fontSize: 10, color: T.purple }} />
                <Box sx={{ width: 1.5, flex: 1, bgcolor: 'rgba(85,37,131,0.12)', my: 0.5, minHeight: 35 }} />
                <PlaceIcon sx={{ fontSize: 16, color: T.purple }} />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <MapboxAddressInput
                  label="Départ" placeholder="D'où partez-vous ?"
                  value={formData.pickup_address} onSelect={handlePickupSelect}
                  icon={null} showGeolocate
                />
                <MapboxAddressInput
                  label="Destination" placeholder="Où allez-vous ?"
                  value={formData.dropoff_address} onSelect={handleDropoffSelect}
                  icon={null} showGeolocate={false}
                />
              </Box>
            </Box>

            {canEstimate && (
              <Box sx={{ mt: 2 }}>
                <MiniMap
                  pickupLat={formData.pickup_latitude} pickupLng={formData.pickup_longitude}
                  dropoffLat={formData.dropoff_latitude} dropoffLng={formData.dropoff_longitude}
                  height={180}
                  onPickupDragEnd={({ latitude, longitude }) => { setFormData(p => ({ ...p, pickup_latitude: latitude, pickup_longitude: longitude })); setEstimate(null); }}
                  onDropoffDragEnd={({ latitude, longitude }) => { setFormData(p => ({ ...p, dropoff_latitude: latitude, dropoff_longitude: longitude })); setEstimate(null); }}
                />
              </Box>
            )}
          </Box>

          {/* Vehicle type */}
          <Box sx={{ mb: 2.5, p: 2.5, bgcolor: T.white, borderRadius: T.radius.card, border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.7rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5 }}>
              Type de véhicule
            </Typography>
            <Grid container spacing={1}>
              {vehicleTypes.map((type) => {
                const selected = formData.vehicle_type === type.value;
                return (
                  <Grid item xs={6} key={type.value}>
                    <Box
                      onClick={() => { setFormData(p => ({ ...p, vehicle_type: type.value })); setEstimate(null); }}
                      sx={{
                        p: 1.5, borderRadius: T.radius.btn, cursor: 'pointer', textAlign: 'center',
                        border: `1px solid ${selected ? T.purple : T.border}`,
                        bgcolor: selected ? 'rgba(85,37,131,0.04)' : 'transparent',
                        transition: T.transition,
                        '&:hover': { borderColor: 'rgba(85,37,131,0.25)' },
                      }}
                    >
                      <DirectionsCarIcon sx={{ fontSize: '1.5rem', mb: 0.3, color: selected ? T.purple : T.muted }} />
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: selected ? T.purple : T.ink }}>{type.label}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: T.muted }}>{type.desc}</Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Estimate button */}
          {!estimate && (
            <Button fullWidth variant="outlined" onClick={calculateEstimate} disabled={!canEstimate}
              sx={{
                mb: 2, py: 1.2, borderRadius: T.radius.btn, fontWeight: 600, fontSize: '0.88rem', textTransform: 'none',
                borderColor: 'rgba(85,37,131,0.2)', color: T.purple, transition: T.transition,
                '&:hover': { borderColor: T.purple, bgcolor: 'rgba(85,37,131,0.03)' },
                '&:disabled': { opacity: 0.35 },
              }}
            >
              Estimer le trajet
            </Button>
          )}

          {/* Estimate result */}
          {estimate && (
            <Box sx={{ mb: 2.5, p: 2, borderRadius: T.radius.btn, bgcolor: 'rgba(85,37,131,0.03)', border: `1px solid ${T.border}` }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.68rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5, textAlign: 'center' }}>
                Estimation du trajet
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <RouteIcon sx={{ color: T.purple, fontSize: 20, mb: 0.3 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: T.ink }}>{estimate.distance} km</Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: T.muted }}>Distance</Typography>
                </Box>
                <Box sx={{ width: '1px', bgcolor: T.border }} />
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <AccessTimeIcon sx={{ color: T.purple, fontSize: 20, mb: 0.3 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: T.ink }}>{estimate.duration} min</Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: T.muted }}>Durée</Typography>
                </Box>
                <Box sx={{ width: '1px', bgcolor: T.border }} />
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <PaymentsIcon sx={{ color: T.purple, fontSize: 20, mb: 0.3 }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: T.purple }}>{estimate.fare} EUR</Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: T.muted }}>Prix estimé</Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Submit */}
          <Button
            type="submit" fullWidth variant="contained" disabled={loading || !estimate}
            startIcon={!loading && <DirectionsCarIcon sx={{ fontSize: '1.1rem !important' }} />}
            sx={{
              py: 1.5, borderRadius: T.radius.btn, fontWeight: 700, fontSize: '0.92rem', textTransform: 'none',
              bgcolor: T.purple, color: 'white', boxShadow: 'none', transition: T.transition,
              '&:hover': { bgcolor: T.purpleDark, boxShadow: 'none' },
              '&:disabled': { bgcolor: 'rgba(85,37,131,0.12)', color: T.muted, boxShadow: 'none' },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Demander une course'}
          </Button>
        </form>
      </Container>
    </Box>
  );
};

export default VTCRequest;
